<script setup>
import { computed } from 'vue';
import { useForm } from 'vee-validate';
import FormInputSignature from '../fields/FormInputSignature.vue';
import FormInputText from '../fields/FormInputText.vue';
import { DateTime } from 'luxon';
import * as yup from 'yup';

const emit = defineEmits(['advance-step']);

const props = defineProps({
    formData: {
        type: Object,
        required: true
    },
    insurer: {
        type: Object,
        required: true
    },
    steps: {
        type: Object,
        required: true
    },
    loading: {
        type: Boolean,
        required: true
    },
    error: {
        type: String,
        required: true
    }
});

const visibleSteps = computed(() => {
    return props.steps.slice(0, -1);
});

const validationSchema = yup.object({
    date: yup
        .string()
        .required('Please enter a date')
        .matches(/^[0123][0-9]\/[01][0-9]\/[0-9]{4}$/, 'Please enter a valid date (DD/MM/YYYY)'),
    signature: yup.string().required('Please enter your signature')
});

const { errors, handleSubmit, defineField, setFieldValue } = useForm({
    validationSchema,
    initialValues: {
        signature: '',
        date: DateTime.now().toFormat('dd/LL/yyyy'),
        ...props.formData
    }
});

const [signature] = defineField('signature');
const [date] = defineField('date');

// It validate all fields and doesn't call your function unless all fields are valid
const onSubmit = handleSubmit((values) => {
    console.log(values);
    emit('advance-step', values);
});
</script>
<template>
    <form @submit="onSubmit">
        <h2 class="panel-heading h2">Declaration</h2>
        <div class="form-panel">
            <ol class="tw-list-none tw-pl-0">
                <li
                    v-for="(step, i) in visibleSteps"
                    :key="step.label"
                    class="tw-pr-14 tw-relative tw-mb-3"
                >
                    <span class="tw-text-lg tw-font-bold">{{ i + 1 }}. {{ step.label }}</span>
                    <svg
                        class="tw-absolute tw-top-0 tw-right-0 tw-w-5 tw-h-5"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 64 50"
                    >
                        <path
                            fill="#9EDB45"
                            d="M57.2 1.2L20 38.4 6.8 25.2c-1.6-1.6-4-1.6-5.6 0-1.6 1.6-1.6 4 0 5.6l16 16c.8.8 1.6 1.2 2.8 1.2s2-.4 2.8-1.2l40-40c1.6-1.6 1.6-4 0-5.6-1.6-1.6-4-1.6-5.6 0z"
                        />
                    </svg>
                </li>
            </ol>
            <p class="tw-mt-8 tw-mb-0">
                I declare that the information provided in this form is true and complete and I
                agree that this Declaration shall be held to form part of the application.
            </p>

            <div class="tw-max-w-[400px] tw-pt-8">
                <FormInputText
                    :value="date"
                    :error="errors.date"
                    name="date"
                    input-classes="tw-pr-14"
                    mask="##/##/####"
                    placeholder="Date (DD/MM/YYYY)"
                    @update="setFieldValue('date', $event)"
                >
                    <!-- prettier-ignore -->
                    <svg version="1.1" class="tw--ml-12" xmlns="http://www.w3.org/2000/svg" width="24" viewBox="0 0 32 32">
                        <path d="M10 12h4v4h-4zM16 12h4v4h-4zM22 12h4v4h-4zM4 24h4v4h-4zM10 24h4v4h-4zM16 24h4v4h-4zM10 18h4v4h-4zM16 18h4v4h-4zM22 18h4v4h-4zM4 18h4v4h-4zM26 0v2h-4v-2h-14v2h-4v-2h-4v32h30v-32h-4zM28 30h-26v-22h26v22z"></path>
                    </svg>
                </FormInputText>

                <FormInputSignature
                    v-model="signature"
                    :error="errors.signature"
                    placeholder="Signature of Life Insured – Type Name Here"
                />
            </div>
        </div>

        <button :class="loading && 'btn--loading'" class="btn btn--green btn--full">
            <span>Submit</span>
        </button>

        <p v-if="props.error" class="tw-text-red tw-mt-8 tw-font-bold">{{ props.error }}</p>
    </form>
</template>
